import React, { ComponentProps } from 'react'

import { User } from '@surglogs-app/icons'
import { Colors } from '@surglogs-app/ui.theme'

import { Icon } from './Icon'
import { Text } from './Text'

export type PillVariant = 'solid' | 'outline' | 'light'

const getLightBackgroundColor = (color: Colors) =>
  (color.split('.')[0] + '.10') as Colors

const getFontColor = (variant: PillVariant, color: Colors) => {
  if (variant === 'outline' || variant === 'light') {
    return color
  }

  if (color === 'white.100') {
    return 'navy.100'
  }

  return 'white.100'
}

const getBackgroundColor = (variant: PillVariant, color: Colors) => {
  if (variant === 'outline') {
    return 'transparent'
  }

  if (variant === 'light') {
    return getLightBackgroundColor(color)
  }

  return color
}

const getBorder = (variant: PillVariant, color: Colors) => {
  let borderColor: Colors = color

  if (color === 'transparent') {
    borderColor = 'white.100'
  }

  if (variant === 'light') {
    borderColor = getLightBackgroundColor(color)
  }

  return {
    bw: 'xxxs',
    bs: 'solid',
    bc: borderColor,
  } as const
}

const getBorderRadius = (isRounded: boolean) => {
  if (isRounded) {
    return 'abs.10'
  }

  return 'xs'
}

export type PillLightColors =
  | 'navy.100'
  | 'red.100'
  | 'orange.100'
  | 'green.100'
  | 'blue.100'
  | 'yellow.100'
  | 'purple.100'

type PillBaseProps = Omit<ComponentProps<typeof Text>, 'variant'> & {
  isRounded?: boolean
  icon?: any
}

type PillProps =
  | (PillBaseProps & {
      variant: 'light'
      color?: PillLightColors
    })
  | (PillBaseProps & { variant?: 'solid' | 'outline'; color?: Colors })

export const Pill = ({
  isRounded = false,
  variant = 'solid',
  color = 'navy.25',
  icon,
  ...rest
}: PillProps) => (
  <Text
    {...{
      icon: User,
      color: getFontColor(variant, color),
      bgc: getBackgroundColor(variant, color),
      px: 'sm',
      py: 'xxs',
      size: 'xxs',
      display: 'inline-flex',
      bold: true,
      gap: 'xs',
      bra: getBorderRadius(isRounded),
      ...getBorder(variant, color),
      ...rest,
    }}>
    {icon ? (
      <Icon {...{ size: 'md', icon, color: getFontColor(variant, color) }} />
    ) : null}
    {rest.children}
  </Text>
)
